import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import QRScanner from '../components/QRScanner';
import TicketStatusChecker from '../components/TicketStatusChecker';
import TicketActivityLog from '../components/TicketActivityLog';

const TicketingStaffDashboard = () => {
  const [todayStats, setTodayStats] = useState({});
  const [printQueue, setPrintQueue] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    fetchTicketingAnalytics();
    fetchTicketTypes();
  }, []);

  const fetchTicketingAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const staffId = localStorage.getItem('staffId') || 'demo-ticketing-staff';
      const response = await axios.get(`${API_BASE_URL}/api/tenant-analytics/ticketing/${staffId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': 'parks'
        }
      });
      
      if (response.data.success) {
        const { data } = response.data;
        setTodayStats({
          todayBookings: data.todayBookings || 0,
          todayRevenue: data.todayRevenue || 0,
          ticketsPrinted: data.ticketsPrinted || 0,
          pendingPrints: data.pendingPrints || 0
        });
        setPrintQueue(data.printQueue || []);
      }
    } catch (error) {
      console.error('Error fetching ticketing analytics:', error);
      // Fallback to mock data
      setTodayStats({ todayBookings: 45, todayRevenue: 9750, ticketsPrinted: 42, pendingPrints: 3 });
      setPrintQueue([
        { id: '1', ticketId: 'TKT001', customerName: 'Raj Kumar', ticketType: 'Adult Entry', quantity: 2, amount: 300, status: 'pending' }
      ]);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/tickets`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': 'parks'
        }
      });
      
      if (response.data.tickets) {
        setTicketTypes(response.data.tickets.map(ticket => ({
          id: ticket._id,
          name: ticket.name,
          price: ticket.price,
          category: ticket.category
        })));
      }
    } catch (error) {
      console.error('Error fetching ticket types:', error);
      // Fallback to mock data
      setTicketTypes([
        { id: '1', name: 'Adult Entry', price: 150, category: 'entry' },
        { id: '2', name: 'Child Entry', price: 100, category: 'entry' }
      ]);
    }
  };

  return (
    <TenantLayout userRole="ticketing_staff">
      <header className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white px-8 py-8 shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2">Ticketing Counter</h1>
            <p className="text-purple-100">Book and print tickets for visitors</p>
          </div>
          <div className="flex space-x-4">
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="text-sm font-medium">Counter: #1</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="text-sm font-medium">Printer: Ready</span>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 p-8 overflow-auto">
        <div className="space-y-8 -mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Today Bookings', value: todayStats.todayBookings || 0, icon: '🎫', gradient: 'from-blue-500 to-cyan-500' },
              { title: 'Today Revenue', value: `₹${(todayStats.todayRevenue || 0).toLocaleString()}`, icon: '💰', gradient: 'from-green-500 to-emerald-500' },
              { title: 'Tickets Printed', value: todayStats.ticketsPrinted || 0, icon: '🖨️', gradient: 'from-purple-500 to-pink-500' },
              { title: 'Pending Prints', value: todayStats.pendingPrints || 0, icon: '⏳', gradient: 'from-orange-500 to-red-500' }
            ].map((stat, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-3">{stat.value}</p>
                  </div>
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${stat.gradient} flex items-center justify-center shadow-lg`}>
                    <span className="text-3xl">{stat.icon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <QRScanner onScanResult={setScanResult} />
              <TicketStatusChecker />
            </div>
            
            <div className="space-y-6">
              <TicketActivityLog />
              
              {scanResult && (
                <div style={{
                  padding: '20px',
                  borderRadius: '16px',
                  background: scanResult.success ? 
                    'linear-gradient(135deg, #d1fae5, #a7f3d0)' : 
                    'linear-gradient(135deg, #fee2e2, #fecaca)',
                  border: `2px solid ${scanResult.success ? '#10b981' : '#ef4444'}`,
                  color: scanResult.success ? '#065f46' : '#991b1b'
                }}>
                  <div style={{ fontWeight: '700', marginBottom: '12px', fontSize: '16px' }}>
                    {scanResult.success ? '✅ Success' : '❌ Error'}
                  </div>
                  <div style={{ marginBottom: '12px' }}>{scanResult.message}</div>
                  {scanResult.data && (
                    <div style={{ fontSize: '14px', background: 'rgba(255,255,255,0.3)', padding: '12px', borderRadius: '8px' }}>
                      <div><strong>Customer:</strong> {scanResult.data.customerName}</div>
                      <div><strong>Booking:</strong> {scanResult.data.bookingId}</div>
                      <div><strong>Location:</strong> {scanResult.data.location}</div>
                      {scanResult.data.entryTime && (
                        <div><strong>Entry:</strong> {new Date(scanResult.data.entryTime).toLocaleString()}</div>
                      )}
                      {scanResult.data.exitTime && (
                        <div><strong>Exit:</strong> {new Date(scanResult.data.exitTime).toLocaleString()}</div>
                      )}
                      {scanResult.data.duration && (
                        <div><strong>Duration:</strong> {scanResult.data.duration}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Booking</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ticketTypes.map(ticket => (
                  <div key={ticket.id} className="p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-xl border border-purple-100 hover:from-purple-50 hover:to-pink-50 transition-all duration-300 cursor-pointer">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-900">{ticket.name}</h4>
                      <span className="text-lg font-bold text-purple-600">₹{ticket.price}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                        {ticket.category}
                      </span>
                      <button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-lg transition-all">
                        Book Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>


            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Print Queue</h3>
              <div className="space-y-4">
                {printQueue.map(item => (
                  <div key={item.id} className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-blue-100">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{item.customerName}</p>
                        <p className="text-sm text-gray-600">{item.ticketType} × {item.quantity}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">₹{item.amount}</span>
                      <button className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded text-sm font-medium hover:shadow-lg transition-all">
                        Print
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
                Print All Pending
              </button>
            </div>
          </div>
        </div>
      </main>
    </TenantLayout>
  );
};

export default TicketingStaffDashboard;